import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, ShoppingCart, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_mak-group-hub/artifacts/j6ljmdmh_logomak.svg";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About Us", path: "/about" },
  { name: "MAK Catalogs", path: "/products" },
];

const companyLinks = [
  { name: "MAK Heavy Equipment Machinery Spare Parts", path: "/companies/mak-spare-parts" },
  { name: "HayalogiX Transport & Equipment Rental", path: "/companies/hayalogix" },
  { name: "Bright Perfect Heavy Equipment", path: "/companies/bright-perfect" },
  { name: "HAMMER MOUNT General Contracting", path: "/companies/hammer-mount" },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [companiesOpen, setCompaniesOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path.split('#')[0]);

  return (
    <>
      {/* Top Bar */}
      <div className="bg-gray-900 text-white py-2 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:+971563419000" className="flex items-center gap-2 hover:text-[#F5B800] transition-colors">
              <Phone size={14} />
              +971 56 341 9000
            </a>
            <a href="mailto:info@makemirates.com" className="flex items-center gap-2 hover:text-[#F5B800] transition-colors">
              <Mail size={14} />
              info@makemirates.com
            </a>
          </div>
          <div className="text-gray-400">
            10 Royal Auto Center, Naif, Deira, Dubai
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-24">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src={LOGO_URL} 
                alt="MAK Group Logo" 
                className="h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-6">
              {navLinks.slice(0, 2).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`font-medium transition-colors duration-200 ${
                    isActive(link.path)
                      ? "text-[#D4A000]"
                      : "text-gray-700 hover:text-[#D4A000]"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Our Companies Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setCompaniesOpen(true)}
                onMouseLeave={() => setCompaniesOpen(false)}
              >
                <button
                  className={`font-medium transition-colors duration-200 flex items-center gap-1 ${
                    location.pathname === "/companies"
                      ? "text-[#D4A000]"
                      : "text-gray-700 hover:text-[#D4A000]"
                  }`}
                  data-testid="nav-our-companies"
                >
                  Our Companies
                  <ChevronDown size={16} className={`transition-transform ${companiesOpen ? 'rotate-180' : ''}`} />
                </button>
                
                <AnimatePresence>
                  {companiesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 mt-2 w-80 bg-white shadow-xl border border-gray-100 py-2 z-50"
                    >
                      {companyLinks.map((company, index) => (
                        <Link
                          key={index}
                          to={company.path}
                          data-testid={`nav-company-${index}`}
                          className="block px-4 py-3 text-gray-700 hover:bg-[#F5B800] hover:text-black transition-colors text-sm font-medium border-b border-gray-50 last:border-b-0"
                        >
                          {company.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {navLinks.slice(2).map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className={`font-medium transition-colors duration-200 ${
                    isActive(link.path)
                      ? "text-[#D4A000]"
                      : "text-gray-700 hover:text-[#D4A000]"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              <Link
                to="/online-store"
                data-testid="nav-store-btn"
                className="relative bg-gray-900 text-white hover:bg-gray-800 font-bold px-5 py-3 transition-all flex items-center gap-2"
              >
                <ShoppingCart size={18} />
                MAK Products
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">
                  NEW
                </span>
              </Link>
              <a
                href="https://wa.me/971563419000"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-whatsapp-btn"
                className="bg-[#25D366] text-white hover:bg-[#20BA5C] font-bold px-5 py-3 transition-all flex items-center gap-2"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className="flex md:hidden text-gray-900 p-2 z-50"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-gray-100"
            >
              <nav className="flex flex-col py-4 px-4">
                {navLinks.slice(0, 2).map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`py-3 px-4 border-b border-gray-100 font-medium transition-colors ${
                      isActive(link.path)
                        ? "text-[#D4A000]"
                        : "text-gray-700 hover:text-[#D4A000]"
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                
                {/* Mobile Companies Submenu */}
                <div className="border-b border-gray-100">
                  <button
                    onClick={() => setCompaniesOpen(!companiesOpen)}
                    className="w-full py-3 px-4 font-medium text-gray-700 flex items-center justify-between"
                    data-testid="mobile-companies-toggle"
                  >
                    Our Companies
                    <ChevronDown size={18} className={`transition-transform ${companiesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  <AnimatePresence>
                    {companiesOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="bg-gray-50 overflow-hidden"
                      >
                        {companyLinks.map((company, index) => (
                          <Link
                            key={index}
                            to={company.path}
                            onClick={() => {
                              setMobileMenuOpen(false);
                              setCompaniesOpen(false);
                            }}
                            data-testid={`mobile-company-${index}`}
                            className="block py-3 px-8 text-sm text-gray-600 hover:text-[#D4A000] border-b border-gray-100 last:border-b-0"
                          >
                            {company.name}
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {navLinks.slice(2).map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`py-3 px-4 border-b border-gray-100 font-medium transition-colors ${
                      isActive(link.path)
                        ? "text-[#D4A000]"
                        : "text-gray-700 hover:text-[#D4A000]"
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                
                <Link
                  to="/online-store"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-store-btn"
                  className="mt-4 bg-gray-900 text-white font-bold px-6 py-4 text-center transition-all flex items-center justify-center gap-2"
                >
                  <ShoppingCart size={18} />
                  MAK Products
                  <span className="bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">NEW</span>
                </Link>
                <a
                  href="https://wa.me/971563419000"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-whatsapp-btn"
                  className="mt-2 bg-[#25D366] text-white font-bold px-6 py-4 text-center transition-all flex items-center justify-center gap-2"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </a>
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                  <a href="tel:+971563419000" className="flex items-center gap-2 text-gray-600 px-4">
                    <Phone size={16} />
                    +971 56 341 9000
                  </a>
                  <a href="mailto:info@makemirates.com" className="flex items-center gap-2 text-gray-600 px-4">
                    <Mail size={16} />
                    info@makemirates.com
                  </a>
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
};
